import { Controller } from "@hotwired/stimulus"

// Cloudflare Turnstile, rendered explicitly.
//
// Cloudflare's drop-in script scans the document for `.cf-turnstile` exactly
// once, when it loads. Under Turbo that is the FIRST page only: a visitor who
// lands on /users/sign_in and clicks through to /users/sign_up reuses the
// script already in <head>, nothing re-scans, and they get a form with no
// challenge and a submit the server will refuse. So the API is loaded in
// explicit mode and the widget is rendered per connect — once per appearance of
// the element, however the visitor got there.
//
// Loading it from here rather than the layout also keeps Cloudflare's script
// off every page that has no challenge on it.
const API_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=widgetoTurnstileReady"

// One script for the whole document, shared by every controller instance and
// kept across Turbo navigations.
let apiLoaded = null

function loadApi() {
  if (window.turnstile) return Promise.resolve()
  if (apiLoaded) return apiLoaded

  apiLoaded = new Promise((resolve) => {
    window.widgetoTurnstileReady = resolve
    const script = document.createElement("script")
    script.src = API_URL
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })

  return apiLoaded
}

export default class extends Controller {
  static targets = ["widget"]
  static values = { siteKey: String, action: String, language: String }

  connect() {
    this.widgetId = null
    loadApi().then(() => this.renderWidget())
  }

  disconnect() {
    // Leaving the widget behind leaks a timer and an iframe on every Turbo
    // navigation away from the form.
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId)
    }
    this.widgetId = null
  }

  renderWidget() {
    // A fast navigation can disconnect us while the script is still loading,
    // and a re-entrant call would stack a second iframe on the same element.
    if (!this.element.isConnected || this.widgetId !== null) return

    const target = this.hasWidgetTarget ? this.widgetTarget : this.element

    this.widgetId = window.turnstile.render(target, {
      sitekey: this.siteKeyValue,
      action: this.actionValue,
      // "auto" lets Turnstile follow the browser; we pass the locale the page
      // is actually rendered in, which is the dashboard's own (en/it).
      language: this.languageValue || "auto"
    })
  }
}
